import { Injectable } from '@angular/core';
import { StorageKeys } from './storage-keys';
import { StorageService } from './storage.service';

export interface DurableOperationIdentity {
  tenantName: string;
  kioskCode: string;
}

/**
 * Binds a durable workflow to the kiosk enrollment that created it.
 *
 * Preferences survives logout/re-enrollment. Without this binding, an old
 * pending payment could be submitted with the HTTP tenant header of a newly
 * configured restaurant.
 */
@Injectable({ providedIn: 'root' })
export class DurableOperationIdentityService {
  constructor(private storage: StorageService) {}

  async captureCurrent(): Promise<DurableOperationIdentity> {
    const tenantName = this.normalize(
      await this.storage.getItemStrict<unknown>(StorageKeys.TENANT_NAME),
    );
    const kioskCode = this.normalize(
      await this.storage.getItemStrict<unknown>(StorageKeys.KIOSK_CODE),
    );
    if (!tenantName || !kioskCode) {
      throw new Error('DURABLE_OPERATION_IDENTITY_MISSING');
    }
    return { tenantName, kioskCode };
  }

  async assertCurrent(expected: DurableOperationIdentity): Promise<void> {
    this.assertShape(expected);
    const current = await this.captureCurrent();
    if (
      current.tenantName !== expected.tenantName ||
      current.kioskCode !== expected.kioskCode
    ) {
      throw new Error('DURABLE_OPERATION_IDENTITY_MISMATCH');
    }
  }

  assertShape(value: unknown): asserts value is DurableOperationIdentity {
    const identity = value as any;
    if (
      !identity ||
      this.normalize(identity.tenantName) !== identity.tenantName ||
      this.normalize(identity.kioskCode) !== identity.kioskCode
    ) {
      throw new Error('INVALID_DURABLE_OPERATION_IDENTITY');
    }
  }

  private normalize(value: unknown): string {
    const normalized = `${value ?? ''}`.trim();
    if (
      !normalized ||
      normalized.length > 128 ||
      /[\0\r\n|]/.test(normalized)
    ) {
      return '';
    }
    return normalized;
  }
}
